import AddIcon from '@mui/icons-material/Add';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseEdge, EdgeLabelRenderer, EdgeProps, getSmoothStepPath, useReactFlow } from 'reactflow';
import useBottomNodeMenuStore from 'stores/bottom-node-menu-store';
import useCampaignNodeStore from 'stores/campaign-node-store';
import { makeStyles } from 'tss-react/mui';
import { findConnectedNodes } from 'utils/flowNode.utils';

const AddButtonEdge = ({
  id,
  target,
  sourceX,
  sourceY,
  source,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  sourceHandleId,
  style = {},
  markerEnd,
}: EdgeProps) => {
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const btnRef = useRef(null);

  const { getNode, getNodes, getEdges, setNodes, setEdges } = useReactFlow();

  const path = sourceHandleId as 'success' | 'failure' | undefined;
  // Only the Ja/Nein branches of a condition can be cut; a linear edge would split the flow in two.
  const isDeletable = path === 'success' || path === 'failure';

  const { selectedNode, setSelectedNode } = useCampaignNodeStore((state) => ({
    selectedNode: state.selectedNode,
    setSelectedNode: state.setSelectedNode,
  }));

  const [orphanedNodeIds, setOrphanedNodeIds] = useState<string[] | null>(null);

  const getHeight = () => {
    const currNode = getNode(source);

    if (!_.isNil(currNode) && !_.isNil(currNode.height)) return currNode.height;

    return undefined;
  };

  const getPosition = () => {
    const currNode = getNode(source);

    if (!_.isNil(currNode) && !_.isNil(currNode.height)) return currNode.position;

    return undefined;
  };

  const handleOpenMenu = useBottomNodeMenuStore((state) => state.handleOpenMenu);

  // Nodes that would hang in the air without this edge. The backend treats every node without
  // an incoming edge as a start step, so they must go with the edge instead of becoming a second flow.
  const getOrphanedNodeIds = () => {
    const edges = getEdges();
    const remainingEdges = edges.filter((edge) => edge.id !== id);
    const rootIds = getNodes()
      .filter((node) => !edges.some((edge) => edge.target === node.id))
      .map((node) => node.id);
    const reachable = new Set(rootIds.flatMap((rootId) => findConnectedNodes(rootId, remainingEdges)));

    return findConnectedNodes(target, remainingEdges).filter((nodeId) => !reachable.has(nodeId));
  };

  const deleteEdge = (nodeIds: string[]) => {
    setEdges((eds) => eds.filter((edge) => edge.id !== id && !nodeIds.includes(edge.source) && !nodeIds.includes(edge.target)));

    if (!_.isEmpty(nodeIds)) {
      setNodes((nds) => nds.filter((node) => !nodeIds.includes(node.id)));

      if (selectedNode && nodeIds.includes(selectedNode.id)) {
        setSelectedNode(null);
      }
    }
  };

  const handleDeleteClick = () => {
    const nodeIds = getOrphanedNodeIds();

    if (_.isEmpty(nodeIds)) {
      deleteEdge([]);
      return;
    }

    setOrphanedNodeIds(nodeIds);
  };

  const handleConfirmDelete = () => {
    deleteEdge(orphanedNodeIds ?? []);
    setOrphanedNodeIds(null);
  };

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div
          className={'nodrag nopan'}
          style={{ display: 'flex', alignItems: 'center', position: 'absolute', transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`, pointerEvents: 'all' }}
        >
          <IconButton
            ref={btnRef}
            className={classes.edgeButton}
            onClick={() => handleOpenMenu(btnRef.current, source, getPosition(), getHeight() || 225, path, true)()}
            disableRipple={true}
          >
            <AddIcon className={classes.icon} />
          </IconButton>
          {isDeletable ? (
            <Tooltip title={t('deleteEdge')}>
              <IconButton className={cx(classes.edgeButton, classes.deleteButton)} onClick={handleDeleteClick} disableRipple={true}>
                <CloseRoundedIcon className={classes.icon} />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
      </EdgeLabelRenderer>

      <Dialog
        classes={{ paper: classes.paper }}
        open={!_.isNil(orphanedNodeIds)}
        fullWidth={true}
        transitionDuration={0}
        maxWidth={'xs'}
      >
        <DialogTitle className={classes.title}>
          <Typography className={classes.titleText}>{t('deleteEdgeTitle')}</Typography>
        </DialogTitle>
        <DialogContent className={classes.content}>
          <Stack gap={1}>
            <Typography className={classes.text}>{t('confirmDeleteEdge1', { count: orphanedNodeIds?.length ?? 0 })}</Typography>
            <Typography className={cx(classes.text, 'highlight')}>{t('confirmDeleteEdge2')}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={() => setOrphanedNodeIds(null)} />
          <PaiButton text={t('ok')} action={handleConfirmDelete} size={'small'} border={'rounded'} />
        </DialogActions>
      </Dialog>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  edgeButton: {
    width: '20px',
    height: '20px',
    background: theme.palette.greys.lighter,
    border: '1px solid #fff',
    borderRadius: '50%',
    lineHeight: 1,
    '&:hover': {
      background: theme.palette.primary.main,
      color: '#FFF',
    },
  },
  deleteButton: {
    marginLeft: '4px',
    '&:hover': {
      background: theme.palette.error.main,
      color: '#FFF',
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(16),
  },
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: `linear-gradient(to right, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
    '&.highlight': {
      fontWeight: 'bold',
      color: theme.palette.primary.main,
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default React.memo(AddButtonEdge);
